import React from 'react'

/**
 * Section tabs (DESIGN.md → Navigation): 15px semibold labels with a count
 * pill, an ink underline under the active tab and a hair-line under the row.
 *
 * Controlled component: the parent owns the active tab id. Wiring the
 * value to the URL (`?tab=…`) is the parent's job — that keeps this
 * primitive routing-agnostic and easy to test in isolation.
 */
export interface TabDef {
  id: string
  label: string
  /** Optional small badge shown after the label, e.g. counts. */
  count?: number | null
  /** Optional tone for the count badge. Defaults to a muted style. */
  tone?: 'neutral' | 'signal'
}

interface TabsProps {
  tabs: TabDef[]
  active: string
  onChange: (id: string) => void
  /** ARIA label for the tablist. */
  ariaLabel?: string
  /** Tighter tab row (company profile). */
  dense?: boolean
  className?: string
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  active,
  onChange,
  ariaLabel,
  dense = false,
  className = '',
}) => {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`print:hidden border-b border-rule flex gap-x-5 sm:gap-x-7 overflow-x-auto [scrollbar-width:none] ${dense ? 'mt-3' : 'mt-6'} ${className}`}
    >
      {tabs.map((t) => {
        const isActive = t.id === active
        return (
          <button
            key={t.id}
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${t.id}`}
            tabIndex={isActive ? 0 : -1}
            type="button"
            onClick={() => onChange(t.id)}
            className={`relative inline-flex shrink-0 items-center gap-[7px] px-0.5 py-3 text-[15px] font-semibold transition-colors ${
              isActive ? 'text-ink' : 'text-ink-500 hover:text-ink'
            }`}
          >
            <span>{t.label}</span>
            {typeof t.count === 'number' && (
              <span
                className={`inline-flex items-center rounded-full px-[7px] py-px text-xs font-semibold tabular-nums ${
                  t.tone === 'signal'
                    ? 'bg-signal-50 text-signal-700'
                    : 'bg-rule-soft text-ink-500'
                }`}
              >
                {t.count}
              </span>
            )}
            {isActive && (
              <span
                aria-hidden
                className="absolute left-0 right-0 bottom-0 h-0.5 bg-ink"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
